import React, { useState } from "react";
import {
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import type Tag from "@/model/interface/Tag";
import TagRow from "./TagRow";
import TagFormDialog, { type TagFormData } from "./TagFormDialog";

const emptyForm: TagFormData = {
  name: "",
  description: "",
  color: "#6B7280",
};

export default function TagsPage() {
  const { data: tags = [], isLoading } = useGetTagsQuery({ all: true });
  const [createTag] = useCreateTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const [deleteTag] = useDeleteTagMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TagFormData>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  const activeTags = tags.filter((t) => t.isActive);
  const inactiveTags = tags.filter((t) => !t.isActive);

  const handleDragStart = (_e: React.DragEvent, id: string) => {
    setDragId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (_e: React.DragEvent, targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }

    const list = [...activeTags];
    const fromIndex = list.findIndex((t) => t.id === dragId);
    const toIndex = list.findIndex((t) => t.id === targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDragId(null);
      return;
    }

    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);

    setDragId(null);

    try {
      await Promise.all(
        list.map((t, i) => updateTag({ id: t.id, data: { sortOrder: i + 1 } as any }).unwrap()),
      );
      toast.success("Order updated");
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (t: Tag) => {
    setEditingId(t.id);
    setForm({
      name: t.name,
      description: t.description ?? "",
      color: t.color,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateTag({ id: editingId, data: form }).unwrap();
        toast.success("Tag updated");
      } else {
        await createTag(form).unwrap();
        toast.success("Tag created");
      }
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to save tag");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTag(deleteTarget.id).unwrap();
      toast.success("Tag removed");
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to delete tag");
    }
  };

  const handleToggleActive = async (t: Tag) => {
    try {
      await updateTag({
        id: t.id,
        data: { isActive: !t.isActive } as any,
      }).unwrap();
      toast.success(t.isActive ? "Tag deactivated" : "Tag activated");
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to update tag");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground text-sm">Manage contact tags for your organisation</p>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tag
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Tags</CardTitle>
          <CardDescription>
            {activeTags.length} tag{activeTags.length !== 1 ? "s" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeTags.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No active tags.</p>
          ) : (
            <div className="divide-y divide-border">
              {activeTags.map((t) => (
                <TagRow
                  key={t.id}
                  tag={t}
                  onEdit={() => openEdit(t)}
                  onDelete={() => setDeleteTarget(t)}
                  onToggleActive={() => handleToggleActive(t)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {inactiveTags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Inactive Tags</CardTitle>
            <CardDescription>
              {inactiveTags.length} deactivated tag{inactiveTags.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {inactiveTags.map((t) => (
                <TagRow
                  key={t.id}
                  tag={t}
                  onEdit={() => openEdit(t)}
                  onDelete={() => setDeleteTarget(t)}
                  onToggleActive={() => handleToggleActive(t)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <TagFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        onFormChange={setForm}
        onSave={handleSave}
        saving={saving}
        isEdit={!!editingId}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{deleteTarget?.name}</span>? This will
              remove the tag from all contacts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
